<template lang="pug">
div
  MHeader(page-title="Audit counts by user")

  div( class="row mb-3" )
    div( class="col-sm-2" )
      label Company:&nbsp;
      select( class="form-control" v-model="companyId")
        option(value="" disabled) Select a company
        option(v-for="c in companyMany" :key="c.id" :value="c.id") {{ c.name }}

    div( class="col-sm-2" )
      label From:&nbsp;
      input( class="form-control" type="date" v-model="fromDate")

    div( class="col-sm-2" )
      label To:&nbsp;
      input( class="form-control" type="date" v-model="toDate")

  div( class="row mb-3" )
    div( class="col-sm-6" )
      span Select a company + date range to see audits completed per user.
    div( class="col-sm-6" )
      button(
        class="btn btn-primary"
        :disabled="!companyId || $apollo.queries.auditCountsByUser.loading"
        v-on:click="refresh"
      )
        | Refresh
        span(v-if="$apollo.queries.auditCountsByUser.loading")
          | &nbsp;
          i.fa.fa-spinner.fa-spin
  button(
    class="btn btn-primary"
    :disabled="!auditCountsByUser || auditCountsByUser.length === 0 || $apollo.queries.auditCountsByUser.loading"
    v-on:click="downloadCsv"
  )
    i.fa.fa-download.mr-1
    | Download CSV
  hr

  .row(v-if="apolloError")
    .col-sm-12
      .alert.alert-danger
        strong Error:&nbsp;
        | {{ apolloError }}

  .row
    .col-sm-12
      table.table.table-striped(v-if="companyId")
        thead
          tr
            th User
            th Email
            th.text-right Batches
            th.text-right Audits

        tbody(v-if="$apollo.queries.auditCountsByUser.loading")
          tr
            td.text-center(colspan="4")
              | Loading&nbsp;
              i.fa.fa-spinner.fa-spin.mr-2

        tbody(v-else-if="!auditCountsByUser || auditCountsByUser.length === 0")
          tr
            td.text-center(colspan="4") No results.

        tbody(v-else)
          tr(v-for="r in auditCountsByUser" :key="r.userId")
            td {{ r.userId }}
            td {{ r.email || '—' }}
            td.text-right {{ (r.batchCount || 0) }}
            td.text-right
              strong {{ (r.auditCount || 0) }}

        tfoot(v-if="auditCountsByUser && auditCountsByUser.length > 0")
          tr
            td(colspan="2")
              strong Total
            td.text-right
              strong {{ totals.batchTotal }}
            td.text-right
              strong {{ totals.auditTotal }}

      .alert.alert-info(v-else)
        | Select a company to load data.
</template>


<style scoped>
label {
  font-weight: bold;
}
</style>

<script>
import Vue from 'vue'
import gql from 'graphql-tag'

export default Vue.extend({
  data() {
    const now = new Date()
    const toISODateInput = (d) => d.toISOString().slice(0, 10)

    const startOfDecJustGone = () => {
      // If current month is Dec (11), use this year's Dec 1; else previous year's Dec 1 (UTC)
      const y = now.getUTCFullYear()
      const m = now.getUTCMonth() // 0-11
      const decYear = m === 11 ? y : y - 1
      return new Date(Date.UTC(decYear, 11, 1, 0, 0, 0))
    }

    return {
      // UI state
      companyId: '',
      fromDate: toISODateInput(startOfDecJustGone()),
      toDate: toISODateInput(new Date()),

      // error state
      apolloError: '',
    }
  },

  computed: {
    totals() {
      const rows = this.auditCountsByUser || []
      const auditTotal = rows.reduce((acc, r) => acc + (Number(r.auditCount) || 0), 0)
      const batchTotal = rows.reduce((acc, r) => acc + (Number(r.batchCount) || 0), 0)
      return { auditTotal, batchTotal }
    },
  },

  apollo: {
    // 1) Load companies for dropdown
    companyMany: {
      query: gql`
        query CompanyDropdown {
        companyMany(sort: { name: ASC }) {
          id
          name
        }
      }
      `,
      // Auto-pick first company
      result({ data }) {
        if (!this.companyId && data?.companyMany?.length) {
          this.companyId = data.companyMany[0].id
        }
      },
    },

    // 2) Load aggregated counts
    auditCountsByUser: {
      query: gql`
        query AuditCountsByUser($companyId: ID!, $from: DateTime!, $to: DateTime!) {
          auditCountsByUser(companyId: $companyId, from: $from, to: $to) {
            userId
            email
            auditCount
            batchCount
          }
        }
      `,
      variables() {
        const from = `${this.fromDate}T00:00:00.000Z`
        const to = `${this.toDate}T23:59:59.999Z`
        return { companyId: this.companyId, from, to }
      },
      skip() {
        return !this.companyId || !this.fromDate || !this.toDate
      },
      update(data) {
        const rows = (data && data.auditCountsByUser) || []
        return rows.map((r) => ({
          ...r,
          auditCount: Number(r.auditCount || 0),
          batchCount: r.batchCount == null ? null : Number(r.batchCount),
        }))
      },
      error(err) {
        this.apolloError = err?.message || String(err)
      },
      fetchPolicy: 'network-only',
    },

  },

  watch: {
    // Auto-reload when inputs change
    companyId() {
      this.refresh()
    },
    fromDate() {
      this.refresh()
    },
    toDate() {
      this.refresh()
    },
  },

  methods: {
    refresh() {
      this.apolloError = ''
      if (this.$apollo && this.$apollo.queries && this.$apollo.queries.auditCountsByUser) {
        this.$apollo.queries.auditCountsByUser.refetch()
      }
    },
    downloadCsv() {
      const rows = (this.auditCountsByUser || []).map((r) => ({
        userId: r.userId,
        email: r.email || '',
        batchCount: r.batchCount == null ? 0 : Number(r.batchCount),
        auditCount: r.auditCount == null ? 0 : Number(r.auditCount),
      }))

      const meta = {
        companyId: this.companyId,
        from: this.fromDate,
        to: this.toDate,
        generatedAt: new Date().toISOString(),
      }

      const header = ['userId', 'email', 'batchCount', 'auditCount']

const lines = [
  header.join(','),
  ...rows.map((r) =>
    header
      .map((k) =>
        this.csvEscape(
          String((r[k] !== null && r[k] !== undefined) ? r[k] : '')
        )
      )
      .join(',')
  ),
]

      const csv = lines.join('\r\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })     

      const filename =
        ('audit-counts-per-user_' +
          this.companyId +
          '_' +
          this.fromDate +
          '_to_' +
          this.toDate +
          '.csv').replace(/[^a-zA-Z0-9._-]/g, '_')      

      // Trigger download
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

    },
    csvEscape(value) {
      // Wrap in quotes if it contains comma, quote, or newline.
      // Double-up quotes per CSV rules.
      if (value == null) return ''
      const s = String(value)
      if (/[",\r\n]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`
      }
      return s
    },
  }
})
</script>
